import { Observable, Subject, Subscription, fromEvent, takeUntil } from 'rxjs';

/**
 * Creates an observable that toggles subscription based on document visibility.
 * This is necessary to prevent memory leaks especially on mobile devices.
 *
 * @param sourceObservable The source observable to subscribe/unsubscribe from
 * @returns An observable that manages visibility-based subscription
 */
export const withVisibilityChange =
  () =>
  <T>(sourceObservable: Observable<T>): Observable<T> => {
    return new Observable<T>(observer => {
      // Track the current subscription
      let currentSubscription: Subscription | null = null;

      // Subject to handle component destruction
      const destroy$ = new Subject<void>();

      // Handle visibility change events
      const visibilityChange$ = fromEvent(document, 'visibilitychange').pipe(takeUntil(destroy$));

      // Subscribe to visibility changes
      visibilityChange$.subscribe(() => {
        if (document.visibilityState === 'visible') {
          // Page is visible - subscribe if not already subscribed
          if (!currentSubscription || currentSubscription.closed) {
            currentSubscription = sourceObservable.subscribe({
              next: value => observer.next(value),
              error: err => observer.error(err),
              complete: () => observer.complete(),
            });
          }
        } else {
          // Page is hidden - unsubscribe to save resources
          if (currentSubscription && !currentSubscription.closed) {
            currentSubscription.unsubscribe();
            currentSubscription = null;
          }
        }
      });

      // Initial subscription if page is visible
      if (document.visibilityState === 'visible') {
        currentSubscription = sourceObservable.subscribe({
          next: value => observer.next(value),
          error: err => observer.error(err),
          complete: () => observer.complete(),
        });
      }

      // Cleanup function
      return () => {
        if (currentSubscription) {
          currentSubscription.unsubscribe();
        }
        destroy$.next();
        destroy$.complete();
      };
    });
  };
